import type { Offering, OrganizationMarketing } from '../../types/offerings';
import type { Result } from '../../types/result';
import { isAdditionalAccreditationHidden } from '../accreditationVisibility';
import { findNextDate } from '../findNextDate';
import { buildMobiusLinkout } from '../buildMobiusLinkout';
import { uuid } from '../../shared/utils';

const GRAD_DEGREE_PATTERN = /^(Master's|Doctorate)$/i;
const isGraduateDegree = (degree?: string) =>
  GRAD_DEGREE_PATTERN.test(degree || '');

export const toResult = (
  offerings: Offering[],
  organizationMarketingData: Record<number, OrganizationMarketing>,
  degree?: string,
  forceLinkoutSchoolIds?: number[],
  publisherIdentityToken?: string,
  mobiusApiUrl?: string,
  organizationMarketingBySlug?: Record<string, OrganizationMarketing>,
): Result[] => {
  const listId = uuid();
  return offerings.map((offering) => {
    const orgId = parseInt(offering.organization.id, 10);
    const orgData =
      organizationMarketingData[orgId] ??
      (Number.isFinite(offering.organization.sourceId)
        ? organizationMarketingData[offering.organization.sourceId]
        : undefined) ??
      (offering.organization.slug
        ? organizationMarketingBySlug?.[offering.organization.slug]
        : undefined);
    const linkoutUrl =
      forceLinkoutSchoolIds && forceLinkoutSchoolIds.includes(orgId)
        ? buildMobiusLinkout(
            offering.id,
            offering.meta.capId,
            offering.meta.audience || '',
            publisherIdentityToken,
            mobiusApiUrl,
          )
        : undefined;
    return {
      schoolName: offering?.organization?.name,
      schoolId: offering?.organization?.id,
      schoolSlug: offering?.organization?.slug,
      city: orgData?.location?.city || orgData?.tags?.city || '',
      state: orgData?.location?.state || orgData?.tags?.state || '',
      programId: offering?.id,
      programName: offering?.name,
      imageURL: orgData?.images?.logo || offering?.imageUrl,
      snippet: offering?.snippet,
      levelOfInstitution: orgData?.additional?.levelOfInstitution || '',
      accreditator: isAdditionalAccreditationHidden(orgData?.additional)
        ? ''
        : orgData?.additional?.accreditator || '',
      percentOnlineEnrollment:
        orgData?.additional?.percentOnlineEnrollment || null,
      isOnline: orgData?.tags?.isOnline ?? false,
      isPublic: orgData?.tags?.isPublic,
      isPrivate: orgData?.tags?.isPrivate,
      forProfit: orgData?.tags?.isForProfit,
      notForProfit: orgData?.tags?.isNotForProfit ?? false,
      applicationCtaLabel: offering?.meta?.applicationCta?.label,
      applicationCtaURL: offering?.meta?.applicationCta?.url,
      programFlow: orgData?.formCustomizations?.programFlow,
      ...(typeof orgData?.formCustomizations?.showActiveProspectScript ===
        'boolean' && {
        showActiveProspectScript:
          orgData.formCustomizations.showActiveProspectScript,
      }),
      tcpa: orgData?.content?.tcpa,
      tcpaSecondParagraph: orgData?.tcpaSecondParagraph,
      confirmationShortIntroText:
        orgData?.content?.confirmation?.meta?.shortIntro,
      confirmationText: orgData?.content?.confirmation?.meta?.text,
      valueProps: orgData?.content?.valueProps,
      programDates: isGraduateDegree(degree)
        ? findNextDate(orgData?.formDates?.grad)
        : findNextDate(orgData?.formDates?.undergrad),
      providerName: offering?.meta?.providerName,
      viewCorrelationId: uuid(),
      embeddedViewCorrelationId: uuid(),
      spotlight: orgData?.content?.spotlight,
      audience: offering.meta.audience,
      capId: offering.meta.capId,
      linkoutUrl,
      degreeSlug: offering.meta?.degree?.slug,
      categorySlug: offering.meta?.category?.slug,
      subjectSlug: offering.meta?.subject?.slug,
      listId,
    };
  });
};
